import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import type { AdditionalContact } from '@/types/contact';

export function useAdditionalContacts(contactId: string) {
  return useApiQuery<AdditionalContact[]>({
    queryKey: [...contactKeys.detail(contactId), 'additional-contacts'],
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/additional-contacts`,
      );
      return ((raw as unknown as { data?: unknown }).data ?? raw) as AdditionalContact[];
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

export function useCreateAdditionalContact(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (dto: Omit<AdditionalContact, 'id'>) =>
      apiPost(operationsClient, `/contacts/${contactId}/additional-contacts`, dto),
    // The dialog surfaces field errors inline (NOT-76); skip the generic toast.
    showErrorToast: false,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...contactKeys.detail(contactId), 'additional-contacts'],
      });
      toast.success('Additional contact created');
    },
  });
}

export function useUpdateAdditionalContact(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({
      additionalContactId,
      ...dto
    }: Partial<AdditionalContact> & { additionalContactId: string }) =>
      apiPatch(
        operationsClient,
        `/contacts/${contactId}/additional-contacts/${additionalContactId}`,
        dto,
      ),
    // The dialog surfaces field errors inline (NOT-76); skip the generic toast.
    showErrorToast: false,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...contactKeys.detail(contactId), 'additional-contacts'],
      });
      toast.success('Additional contact updated');
    },
  });
}

export function useDeleteAdditionalContact(contactId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (additionalContactId: string) =>
      apiDelete(
        operationsClient,
        `/contacts/${contactId}/additional-contacts/${additionalContactId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...contactKeys.detail(contactId), 'additional-contacts'],
      });
      toast.success('Additional contact deleted');
    },
  });
}
